// src/hooks/useAuth.js
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';

// Nome do evento customizado que usaremos para comunicação
const AUTH_STATE_CHANGE_EVENT = 'authStateChanged';

const useAuth = () => {
  const [user, setUser] = useState(() => {
    try {
      const storedUser = localStorage.getItem('user');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch {
      return null;
    }
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  
  const updateUserAndNotify = useCallback((newUserData) => {
    setUser(newUserData);
    localStorage.setItem('user', JSON.stringify(newUserData));
    window.dispatchEvent(new CustomEvent(AUTH_STATE_CHANGE_EVENT));
  }, []);

  const logout = useCallback((redirectPath = '/login', options = { replace: true }) => {
    console.log(`[useAuth] Efetuando logout. Redirecionando para: ${redirectPath}`);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setError(null);
    navigate(redirectPath, options);
  }, [navigate]);

  const verifyAuth = useCallback(async (isInitialLoad = false) => {
    const token = localStorage.getItem('token');
    console.log(`[useAuth] verifyAuth: Iniciando. Token existente: ${token ? 'Sim' : 'Não'}, É carga inicial? ${isInitialLoad}`);

    if (!token) {
      console.log('[useAuth] verifyAuth: Nenhum token encontrado.');
      setUser(null);
      setIsLoading(false);
      if (!isInitialLoad && window.location.pathname !== '/login') {
         console.warn('[useAuth] verifyAuth: Token desapareceu após a carga inicial, executando logout.');
         logout();
      }
      return;
    }

    if (isInitialLoad) {
      setIsLoading(true);
    }

    try {
      console.log('[useAuth] verifyAuth: Tentando buscar usuário com authService.getMe()');
      // O authService.getMe() deve agora retornar o usuário com dados de assinatura populados
      const userData = await authService.getMe(); 
      console.log('[useAuth] verifyAuth: Dados do usuário recebidos:', userData);

      if (!userData) {
        console.error('[useAuth] verifyAuth: authService.getMe() retornou dados nulos/vazios.');
        throw new Error('Dados do usuário não recebidos do servidor.');
      }

      updateUserAndNotify(userData);
      setError(null);
    } catch (err) {
      console.error('[useAuth] verifyAuth: Erro ao buscar usuário (authService.getMe falhou):', JSON.stringify(err));
      setError(err.message || 'Falha na verificação de autenticação.');
      if (err && err.status === 401) {
         console.warn('[useAuth] verifyAuth: Erro 401 capturado, chamando logout.');
         logout(); 
      } else {
         console.warn('[useAuth] verifyAuth: Erro ao buscar usuário, mas não é 401. Não deslogando automaticamente.', err.status);
      }
    } finally {
      console.log('[useAuth] verifyAuth: Finalizado.');
      setIsLoading(false);
    }
  }, [logout, updateUserAndNotify]);

  useEffect(() => {
    console.log('[useAuth] useEffect inicial: Chamando verifyAuth.');
    verifyAuth(true);
  }, [verifyAuth]);

  useEffect(() => {
    const handleAuthStateChange = () => {
      console.log('[useAuth] Evento authStateChanged recebido! Sincronizando estado.');
      const storedUser = localStorage.getItem('user');
      setUser(storedUser ? JSON.parse(storedUser) : null);
    };

    window.addEventListener(AUTH_STATE_CHANGE_EVENT, handleAuthStateChange);

    return () => {
      window.removeEventListener(AUTH_STATE_CHANGE_EVENT, handleAuthStateChange);
    };
  }, []);

  const login = useCallback((userDataFromApi, redirectPath = '/dashboard') => {
    console.log(`[useAuth] Função login CHAMADA. userDataFromApi:`, userDataFromApi, `Redirecionando para: ${redirectPath}`);
    const tokenExistente = localStorage.getItem('token');
    console.log(`[useAuth] login: Verificando token no localStorage AGORA: ${tokenExistente ? 'Sim' : 'Não'}`);

    if (!tokenExistente) {
      console.error("[useAuth] login: ALERTA! NENHUM TOKEN ENCONTRADO NO LOCALSTORAGE após a chamada da função login.");
      setError("Falha no login: token não encontrado.");
      setIsLoading(false); 
      return; 
    }

    if (userDataFromApi) {
      console.log('[useAuth] login: Usando userDataFromApi para definir o usuário.', userDataFromApi);
      updateUserAndNotify(userDataFromApi);
      setIsLoading(false); 
    } else {
      console.log('[useAuth] login: userDataFromApi não fornecido. O próximo verifyAuth buscará o usuário.');
    }
    
    setError(null); 
    console.log(`[useAuth] login: Navegando para ${redirectPath}`);
    navigate(redirectPath, { replace: true });
  }, [navigate, updateUserAndNotify]);

  return {
    user,
    isLoading,
    error,
    login,
    logout,
    isAuthenticated: !!user && !!localStorage.getItem('token'),
    verifyAuth,
    updateUserAndNotify 
  };
};

export default useAuth;